<template>
  <div id="app">
    <!-- 🔝 Top Navigation Bar -->
    <header v-if="showTopBar" class="top-bar">
      <button
        v-if="!isDashboard"
        class="back-btn"
        @click="goBack"
        title="Retour"
      >
        <i class="bi bi-arrow-left"></i>
      </button>

      <h1 class="page-title">{{ pageTitle }}</h1>

      <button
        v-if="isAuthenticated"
        class="logout-btn"
        @click="logout"
        title="Déconnexion"
      >
        <i class="bi bi-box-arrow-right"></i>
      </button>
    </header>

    <!-- 📄 Main Content -->
    <main :class="['main-content', { 'has-top-bar': showTopBar, 'has-bottom-tabs': showTabs }]">
      <router-view />
    </main>

    <!-- 📱 Bottom Tab Bar (mobile navigation) -->
    <nav v-if="showTabs" class="bottom-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.path"
        class="tab-item"
        :class="{ active: isActive(tab.path) }"
        @click="goTo(tab.path)"
      >
        <span class="tab-icon">{{ tab.icon }}</span>
        <span class="tab-label">{{ tab.label }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "App",
  data() {
    return {
      tabs: [
        { path: "/", label: "Accueil", icon: "🏠" },
        { path: "/coffee-stock", label: "Café", icon: "☕" },
        { path: "/cash-income", label: "Recettes", icon: "💰" },
        { path: "/expenses", label: "Charges", icon: "💸" },
        { path: "/monthly-report", label: "Rapport", icon: "📊" },
      ],
    };
  },
  computed: {
    ...mapGetters({ isAuthenticated: "auth/isAuthenticated" }),
    isDashboard() {
      return this.$route.path === "/";
    },
    isLogin() {
      return this.$route.path === "/login";
    },
    showTopBar() {
      return this.isAuthenticated;
    },
    showTabs() {
      return this.isAuthenticated;
    },
    pageTitle() {
      const titles = {
        Dashboard: "Tableau de bord",
        CoffeeStock: "Stock Café",
        CashIncome: "Recettes",
        Expenses: "Charges",
        MonthlyReport: "Rapport Mensuel",
      };
      return titles[this.$route.name] || "Café Manager";
    },
  },
  methods: {
    ...mapActions("auth", ["logoutUser"]),
    goTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    logout() {
      this.logoutUser();
      this.$router.push("/login");
    },
    isActive(path) {
      return this.$route.path === path;
    },
  },
};
</script>

<style>
/* 🌍 Base */
#app {
  font-family: "Poppins", Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  position: relative;
}

/* 🔝 Top Bar */
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 52px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}

.back-btn,
.logout-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}
.back-btn:hover,
.logout-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
  flex: 1;
  text-align: center;
}

/* 📄 Main Content */
.main-content {
  min-height: 100vh;
}
.main-content.has-top-bar {
  padding-top: 52px;
}
.main-content.has-bottom-tabs {
  padding-bottom: 64px;
}

/* 📱 Bottom Tabs */
.bottom-tabs {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 100;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  padding-bottom: env(safe-area-inset-bottom);
}

.tab-item {
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: pointer;
  color: #a0aec0;
  transition: color 0.2s ease;
  padding: 4px 0;
}
.tab-item.active {
  color: #667eea;
}
.tab-item:active {
  opacity: 0.7;
}

.tab-icon {
  font-size: 20px;
  line-height: 1;
}
.tab-label {
  font-size: 11px;
  font-weight: 500;
}

/* ✨ Fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
